/**
 * lib/hr.ts
 * ------------------------------------------------------------------
 * HR DOMAIN — types, constants, and pure helpers.
 *
 * This is the second domain on the LKC platform. It shares the
 * shell, auth, theming, and infrastructure with the academic domain
 * but does NOT entangle with the academic data model.
 *
 * The ONLY bridge between the domains is `HrStaff.linkedTeacherId`,
 * which points at an existing academic teacher record (reportTeachers).
 * Academic screens never query HR; HR screens never query marks.
 *
 * Firestore collections are all prefixed `hr*` (mirrors the academic
 * `report*` convention). Timestamps are stored as ISO strings
 * (`new Date().toISOString()`), matching the rest of the platform.
 *
 * Everything in this module is removable: delete the HR nav items,
 * the HR routes, and these collections, and the academic platform is
 * exactly as it was.
 * ------------------------------------------------------------------ */

/* ==================================================================
   COLLECTION NAMES
   ================================================================== */

export const HR_COLLECTIONS = {
  staff: 'hrStaff',
  complianceDocs: 'hrComplianceDocs',
  complianceMatrix: 'hrComplianceMatrix',
  attendance: 'hrAttendance',
  leave: 'hrLeave',
  contracts: 'hrContracts',
  appraisals: 'hrAppraisals',
  payrollRefs: 'hrPayrollRefs',
} as const;

/* ==================================================================
   STAFF
   ================================================================== */

export type StaffCategory =
  | 'teaching'
  | 'admin'
  | 'support'
  | 'management'
  | 'ancillary';

export type StaffStatus = 'active' | 'on_leave' | 'suspended' | 'exited';

export type ResidencyStatus = 'citizen' | 'work_permit' | 'residence' | 'pr';

export interface HrStaff {
  id: string;
  /** Employee number used by the school / payroll provider. */
  employeeNo: string;
  firstName: string;
  /** Optional middle name(s). */
  middleName?: string;
  lastName: string;
  category: StaffCategory;
  /** Job title, e.g. "Mathematics Teacher", "Bursar", "Groundsman". */
  jobTitle: string;
  /** Department or unit, e.g. "Sciences", "Finance", "Estates". */
  department?: string;
  email?: string;
  phone?: string;
  residency: ResidencyStatus;
  status: StaffStatus;
  startDate: string; // ISO
  /**
   * THE BRIDGE. For teaching staff only: the id of the academic
   * teacher record (reportTeachers) this person corresponds to.
   * Non-teaching staff leave this undefined and never touch the
   * academic domain.
   */
  linkedTeacherId?: string;
  photoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

/* ==================================================================
   COMPLIANCE DOCUMENTS
   ------------------------------------------------------------------
   ONE model + ONE expiry engine for every document type. Permission
   to Teach applies to every teacher; permits apply to expats; police
   clearance / first aid / safeguarding may apply broadly. They all
   share one shape.
   ================================================================== */

export type ComplianceDocType =
  | 'permission_to_teach'
  | 'work_permit'
  | 'residence_permit'
  | 'permanent_residence'
  | 'police_clearance'
  | 'teaching_qualification'
  | 'first_aid'
  | 'safeguarding'
  | 'medical_fitness'
  | 'other';

export type ComplianceStatus =
  | 'active'
  | 'pending_renewal'
  | 'expired'
  | 'not_required';

export interface ComplianceHistoryEntry {
  referenceNumber: string;
  issueDate: string;
  expiryDate?: string;
  fileUrl?: string;
  supersededAt: string;
}

export interface HrComplianceDoc {
  id: string;
  staffId: string;
  docType: ComplianceDocType;
  docTypeLabel: string;
  issuingAuthority?: string;
  referenceNumber?: string;
  issueDate?: string;
  /** Nullable — some documents (e.g. a degree, PR) don't expire. */
  expiryDate?: string;
  status: ComplianceStatus;
  fileUrl?: string;
  notes?: string;
  /** Renewal chain — successive records of the same document. */
  history?: ComplianceHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

/** Which doc types are mandatory for which staff category. */
export interface HrComplianceMatrixEntry {
  id: string;
  category: StaffCategory;
  docType: ComplianceDocType;
  mandatory: boolean;
}

/* ==================================================================
   ATTENDANCE / CLOCKING
   ------------------------------------------------------------------
   Fingerprints are matched on the clocking terminals themselves. The
   ONE way punches enter the portal is the terminals' "Attendance
   Record" export, uploaded on the Clocking Upload screen
   (features/hr/ClockingImport, parsed by lib/clockingGrid) by the
   account that holds canImportClocking. The export carries only a
   terminal user ID and times, so we never hold biometric data at all
   (Botswana Data Protection Act 2018).
   ================================================================== */

export type PunchType = 'in' | 'out';
export type PunchMethod = 'face' | 'fingerprint' | 'card' | 'manual';
export type AttendanceDayStatus =
  | 'present'
  | 'late'
  | 'absent'
  | 'leave'
  | 'half_day';

export interface AttendancePunch {
  type: PunchType;
  at: string; // ISO
  method: PunchMethod;
  deviceId?: string;
}

export interface HrAttendance {
  id: string; // `${staffId}_${date}`
  staffId: string;
  date: string; // YYYY-MM-DD
  punches: AttendancePunch[];
  totalHours: number;
  status: AttendanceDayStatus;
}

/* ==================================================================
   LEAVE
   ================================================================== */

export type LeaveType =
  | 'annual'
  | 'sick'
  /**
   * Its own entitlement, separate from ordinary sick leave. Botswana's
   * Employment and Labour Relations Act 2025, in force from 1 September
   * 2026, creates it as a distinct 20-day right — so a member of staff
   * hospitalised does not spend the sick leave they will still need for
   * ordinary illness afterwards.
   */
  | 'hospitalisation'
  | 'maternity'
  | 'paternity'
  | 'compassionate'
  | 'study'
  | 'unpaid';

export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface HrLeaveRequest {
  id: string;
  staffId: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  days: number;
  reason?: string;
  status: LeaveStatus;
  approverId?: string;
  decidedAt?: string;
  createdAt: string;
}

/* ==================================================================
   CONTRACTS
   ================================================================== */

export type ContractType = 'permanent' | 'fixed_term' | 'probation' | 'casual';

/** Which end-of-service benefit a contract pays on exit. Statutory severance
 *  and contractual gratuity are mutually exclusive; 'none' pays neither. */
export type EndOfServiceBenefit = 'gratuity' | 'severance' | 'none';

export const END_OF_SERVICE_BENEFIT_LABELS: Record<EndOfServiceBenefit, string> = {
  gratuity: 'Contractual gratuity',
  severance: 'Statutory severance',
  none: 'None',
};

export interface HrContract {
  id: string;
  staffId: string;
  type: ContractType;
  startDate: string;
  endDate?: string;
  fileUrl?: string;
  /** Versioned: most recent is current; previous kept for the record. */
  version: number;
  active: boolean;
  notes?: string;
  /** End-of-service benefit this contract pays on exit. Absent on older
   *  contracts; resolveExitBenefit() falls back to the gratuity flag then. */
  endOfServiceBenefit?: EndOfServiceBenefit;
  createdAt: string;
}

/* ==================================================================
   APPRAISALS
   ================================================================== */

export type AppraisalStatus = 'draft' | 'in_review' | 'completed';

export interface HrAppraisal {
  id: string;
  staffId: string;
  cycle: string; // e.g. "2026-H1"
  reviewerId?: string;
  rating?: number; // 1-5
  status: AppraisalStatus;
  summary?: string;
  createdAt: string;
}

/* ==================================================================
   PAYROLL REFERENCES — references only, NOT a payroll engine.
   We hold salary band + provider reference and export timesheets to
   the school's existing payroll provider's format. We do NOT compute
   PAYE / run payslips / BURS compliance — that stays with the school's
   provider.
   ================================================================== */

export interface HrPayrollRef {
  id: string; // == staffId
  staffId: string;
  salaryBand?: string;
  /** Reference id in the school's external payroll system. */
  providerRef?: string;
  bankName?: string;
  notes?: string;
  updatedAt: string;
}

/* ==================================================================
   LABELS (display)
   ================================================================== */

export const DOC_TYPE_LABELS: Record<ComplianceDocType, string> = {
  permission_to_teach: 'Permission to Teach',
  work_permit: 'Work Permit',
  residence_permit: 'Residence Permit',
  permanent_residence: 'Permanent Residence',
  police_clearance: 'Police Clearance',
  teaching_qualification: 'Teaching Qualification',
  first_aid: 'First Aid Certificate',
  safeguarding: 'Safeguarding Certificate',
  medical_fitness: 'Medical Fitness',
  other: 'Other Document',
};

export const STAFF_CATEGORY_LABELS: Record<StaffCategory, string> = {
  teaching: 'Teaching',
  management: 'Management',
  admin: 'Administration',
  support: 'Support',
  ancillary: 'Ancillary',
};

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  annual: 'Annual',
  sick: 'Sick',
  hospitalisation: 'Hospitalisation',
  maternity: 'Maternity',
  paternity: 'Paternity',
  compassionate: 'Compassionate',
  study: 'Study',
  unpaid: 'Unpaid',
};

export const RESIDENCY_LABELS: Record<ResidencyStatus, string> = {
  citizen: 'Citizen',
  work_permit: 'Work Permit',
  residence: 'Residence',
  pr: 'Permanent Residence',
};

/* ==================================================================
   PURE HELPERS
   ================================================================== */

export function staffFullName(
  s: Pick<HrStaff, 'firstName' | 'middleName' | 'lastName'>,
): string {
  return [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' ').trim();
}

export function staffInitials(
  s: Pick<HrStaff, 'firstName' | 'lastName'>,
): string {
  return `${s.firstName?.[0] ?? ''}${s.lastName?.[0] ?? ''}`.toUpperCase();
}

/** Days from today until `iso` (negative = already past). */
export function daysUntil(iso?: string, now: Date = new Date()): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const ms = target - now.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export type DocHealth = 'ok' | 'expiring' | 'expired' | 'missing';

/**
 * Resolve the practical health of one compliance document. This is
 * the same logic the scheduled expiry engine (complianceExpiryAlerts)
 * uses — kept pure so UI and Cloud Function agree.
 */
export function docHealth(
  doc: Pick<HrComplianceDoc, 'status' | 'expiryDate'>,
  warnWithinDays = 90,
  now: Date = new Date(),
): DocHealth {
  if (doc.status === 'expired') return 'expired';
  if (doc.status === 'not_required') return 'ok';
  const d = daysUntil(doc.expiryDate, now);
  if (d === null) return 'ok'; // no expiry = doesn't lapse
  if (d < 0) return 'expired';
  if (d <= warnWithinDays) return 'expiring';
  return 'ok';
}

export interface StaffComplianceSummary {
  staffId: string;
  expired: number;
  expiring: number;
  missingMandatory: number;
  ok: number;
  /** Worst health across all of this staff member's documents. */
  worst: DocHealth;
}

/**
 * Roll a staff member's documents (plus the mandatory matrix for
 * their category) into a single health summary. `missingMandatory`
 * counts doc types the matrix marks mandatory for which no record
 * exists.
 */
export function summariseStaffCompliance(
  staff: HrStaff,
  docs: HrComplianceDoc[],
  matrix: HrComplianceMatrixEntry[],
  now: Date = new Date(),
): StaffComplianceSummary {
  const mine = docs.filter((d) => d.staffId === staff.id);
  let expired = 0;
  let expiring = 0;
  let ok = 0;

  for (const d of mine) {
    const h = docHealth(d, 90, now);
    if (h === 'expired') expired += 1;
    else if (h === 'expiring') expiring += 1;
    else ok += 1;
  }

  const mandatoryTypes = matrix
    .filter((m) => m.category === staff.category && m.mandatory)
    .map((m) => m.docType);
  const heldTypes = new Set(mine.map((d) => d.docType));
  const missingMandatory = mandatoryTypes.filter(
    (t) => !heldTypes.has(t),
  ).length;

  let worst: DocHealth = 'ok';
  if (expired > 0) worst = 'expired';
  else if (missingMandatory > 0) worst = 'missing';
  else if (expiring > 0) worst = 'expiring';

  return { staffId: staff.id, expired, expiring, missingMandatory, ok, worst };
}


/* ---- staff number generation (lkc-t-001 / lkc-nt-001) ---------------- *
 * Teaching staff are 'lkc-t-NNN'; every other category is 'lkc-nt-NNN'.
 * Canonical assignment orders by start date (oldest = 001) per group;
 * new staff take the next number in their group. -------------------- */
const staffTag = (category: StaffCategory): 't' | 'nt' => (category === 'teaching' ? 't' : 'nt');

export function formatStaffNumber(category: StaffCategory, n: number): string {
  return `lkc-${staffTag(category)}-${String(n).padStart(3, '0')}`;
}

type StaffNumInput = { id: string; employeeNo?: string; category: StaffCategory; startDate?: string };

/** Next number in a category's sequence (highest existing in group + 1). */
export function nextStaffNumber(category: StaffCategory, staff: StaffNumInput[]): string {
  const prefix = `lkc-${staffTag(category)}-`;
  const nums = staff
    .filter((s) => (s.employeeNo ?? '').toLowerCase().startsWith(prefix))
    .map((s) => parseInt((s.employeeNo ?? '').replace(/\D/g, ''), 10))
    .filter((n) => !Number.isNaN(n));
  return formatStaffNumber(category, (nums.length ? Math.max(...nums) : 0) + 1);
}

/** One-time canonical renumber: oldest start date = 001, per group. */
export function canonicalStaffNumbers(staff: StaffNumInput[]): { id: string; employeeNo: string }[] {
  const bySeniority = (a: StaffNumInput, b: StaffNumInput) =>
    String(a.startDate ?? '').localeCompare(String(b.startDate ?? '')) || String(a.id).localeCompare(String(b.id));
  const out: { id: string; employeeNo: string }[] = [];
  for (const teaching of [true, false]) {
    staff
      .filter((s) => (s.category === 'teaching') === teaching)
      .sort(bySeniority)
      .forEach((s, i) => out.push({ id: s.id, employeeNo: formatStaffNumber(s.category, i + 1) }));
  }
  return out;
}
